import type { AbilityHotkey } from '../data/abilities.js';
import type { DoctrineId } from '../data/doctrines.js';
import type { PlayerView, Unsubscribe } from '../matchHost/MatchHost.js';
import type { AbilityTargetRef } from '../simulation/commands/AbilityCommand.js';
import { makeAbilityCommand } from '../simulation/commands/AbilityCommand.js';
import { makeAttackMoveCommand } from '../simulation/commands/AttackMoveCommand.js';
import { makeBuildCommand } from '../simulation/commands/BuildCommand.js';
import type { Command } from '../simulation/commands/Command.js';
import { makeMoveCommand } from '../simulation/commands/MoveCommand.js';
import { makePickDoctrineCommand } from '../simulation/commands/PickDoctrineCommand.js';
import { makeProduceSquadCommand } from '../simulation/commands/ProduceSquadCommand.js';
import type { PermanentBuildingKind } from '../simulation/entities/Building.js';
import type { SquadArchetype } from '../simulation/entities/Squad.js';
import type { EntityId, PlayerId } from '../simulation/ids.js';
import { tickOf } from '../simulation/ids.js';
import type { Vec2 } from '../utils/vec2.js';

import type { CommandSource, CommandSourceIo } from './CommandSource.js';

// Translates input intents into Commands. The client's InputRouter and HUD call the
// request* methods; they never reach into systems or sprites. Bots will emit the same
// Command shapes via BotCommandSource — humans and bots share this contract by design
// (Rule 6).
//
// V2 Tempo: optional `onProduceSquad` hook lets the client (browser) layer
// observe player-issued squad production for the SquadMastery counter
// without requiring a sim-side per-archetype stat. The shared layer
// stays DOM-free; the client wires this in ClientApp.
export type ProduceSquadObserver = (archetype: SquadArchetype) => void;
export class HumanCommandSource implements CommandSource {
  // Mutable so the network client can update it after the server's
  // slot-assignment message arrives (the V2 client constructs this
  // with PLAYER_ZERO at app boot, before knowing which slot the
  // server will hand out — see `apps/client/src/game/ClientApp.ts`
  // `startNetworkMatch`). Server-side command re-stamping in
  // `apps/server/src/rooms/SignalClashRoom.ts` is the authoritative
  // fix for `issuedBy`, but keeping this field accurate avoids
  // misleading client-side logs and keeps Local mode correct.
  playerId: PlayerId;
  private emit: ((cmd: Command) => void) | null = null;
  private unsubSnapshot: Unsubscribe | null = null;
  private latestTick = 0;
  // V2 Tempo — optional client-side hook fired every time the player
  // queues a squad. Used for SquadMastery counting.
  private produceObserver: ProduceSquadObserver | null = null;

  constructor(playerId: PlayerId) {
    this.playerId = playerId;
  }

  setPlayerId(playerId: PlayerId): void {
    this.playerId = playerId;
  }

  // V2 Tempo — install a listener for produce-squad commands. The
  // browser ClientApp wires this to apps/client/src/save/SquadMastery
  // so every Recon/Assault/Engineer/Shield the player queues ticks the
  // per-archetype mastery counter. Setting null clears the observer.
  setProduceObserver(observer: ProduceSquadObserver | null): void {
    this.produceObserver = observer;
  }

  start(io: CommandSourceIo): void {
    this.emit = io.emit;
    this.unsubSnapshot = io.onSnapshot((snap: PlayerView) => {
      this.latestTick = snap.tick;
    });
  }

  stop(): void {
    this.unsubSnapshot?.();
    this.unsubSnapshot = null;
    this.emit = null;
  }

  requestMove(squadIds: EntityId[], target: Vec2): void {
    if (this.emit === null || squadIds.length === 0) return;
    this.emit(
      makeMoveCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        squadIds,
        target,
      }),
    );
  }

  requestAttackMove(squadIds: EntityId[], target: Vec2): void {
    if (this.emit === null || squadIds.length === 0) return;
    this.emit(
      makeAttackMoveCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        squadIds,
        target,
      }),
    );
  }

  requestProduceSquad(producerId: EntityId, archetype: SquadArchetype): void {
    if (this.emit === null) return;
    this.emit(
      makeProduceSquadCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        producerId,
        archetype,
      }),
    );
    // V2 Tempo — surface to the client mastery layer. Counted on COMMAND
    // emission, not on actual squad spawn — close enough for a
    // player-facing counter, and avoids reading the snapshot.
    this.produceObserver?.(archetype);
  }

  requestBuild(coreId: EntityId, slotIndex: number, building: PermanentBuildingKind): void {
    if (this.emit === null) return;
    this.emit(
      makeBuildCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        coreId,
        slotIndex,
        building,
      }),
    );
  }

  requestAbility(
    squadId: EntityId,
    target: AbilityTargetRef | null = null,
    hotkey: AbilityHotkey = 'Q',
  ): void {
    if (this.emit === null) return;
    this.emit(
      makeAbilityCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        squadId,
        hotkey,
        target,
      }),
    );
  }

  requestPickDoctrine(doctrineId: DoctrineId): void {
    if (this.emit === null) return;
    this.emit(
      makePickDoctrineCommand({
        issuedBy: this.playerId,
        tick: tickOf(this.latestTick + 1),
        doctrineId,
      }),
    );
  }
}
